import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ItemCard from '@/components/ItemCard';
import SearchBar from '@/components/SearchBar';
import { MapPin, RefreshCw, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicView = () => {
  const [searchParams] = useSearchParams();
  const location = searchParams.get('location') || 'zlin';
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = inventory.filter(item =>
        item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.slot_id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredInventory(filtered);
    } else {
      setFilteredInventory(inventory);
    }
  }, [searchQuery, inventory]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/${location}`);
      setInventory(response.data);
      setFilteredInventory(response.data);
      
      const lastUpdatedResponse = await axios.get(`${API}/inventory/${location}/last-updated`);
      if (lastUpdatedResponse.data.last_updated) {
        setLastUpdated(new Date(lastUpdatedResponse.data.last_updated));
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="public-view">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/10 bg-secondary/50 backdrop-blur-md sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-white" data-testid="location-header">
                  Pokémat - {location.charAt(0).toUpperCase() + location.slice(1)}
                </h1>
              </div>
              <p className="text-muted-foreground text-base md:text-lg">
                Podívej se, co na tebe čeká v automatu!
              </p>
            </div>
            
            {lastUpdated && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="last-updated">
                <RefreshCw className="w-4 h-4" />
                <span>
                  Aktualizováno: {format(lastUpdated, 'dd. MM. yyyy HH:mm', { locale: cs })}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stock Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3"
          data-testid="stock-warning"
        >
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-500 text-sm leading-relaxed">
            <strong>Upozornění:</strong> Stav skladu se neaktualizuje v reálném čase. Některé produkty v automatu mohou být již vyprodané.
          </p>
        </motion.div>

        <SearchBar value={searchQuery} onChange={setSearchQuery} />

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <p className="text-muted-foreground text-lg">
              {searchQuery ? 'Žádné výsledky nenalezeny' : 'Momentálně nejsou k dispozici žádné položky'}
            </p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: { staggerChildren: 0.05 }
              }
            }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
            data-testid="inventory-grid"
          >
            {filteredInventory.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-20 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-muted-foreground text-sm">
          <p>© 2026 Pokémat. Všechna práva vyhrazena.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicView;